import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Search as SearchIcon } from "lucide-react";
import analytics from "../lib/analytics";

const sectionDefs = [
  { key: "protocols", title: "Protocols", routePrefix: "/app/protocols", idField: "protocol_id", titleField: "title", metaField: "category" },
  { key: "drugs", title: "Drugs", routePrefix: "/app/drugs", idField: "drug_id", titleField: "name", metaField: "drug_class" },
  { key: "paediatric_drugs", title: "Paediatric doses", routePrefix: "/app/paediatric-drugs", idField: "id", titleField: "name", metaField: "category", listOnly: true },
  { key: "antibiotic_guide", title: "Antibiotic guide", routePrefix: "/app/antibiotic-guide", idField: "guide_id", titleField: "condition", metaField: "category", listOnly: true },
  { key: "calculators", title: "Calculators", routePrefix: "/app/calculators", idField: "id", titleField: "name", metaField: "category" },
  { key: "icd10", title: "ICD-10 codes", routePrefix: "/app/icd10", idField: "code", titleField: "description", metaField: "category", listOnly: true },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const initialQ = params.get("q") || "";
  const [q, setQ] = useState(initialQ);
  const [results, setResults] = useState(null);
  const lastTracked = useRef("");

  const doSearch = useCallback(async (query) => {
    if (!query.trim()) return setResults(null);
    const { data } = await api.get("/search", { params: { q: query } });
    setResults(data);
    // GA4 search — fire once per settled term (dedupe rapid keystrokes).
    const term = query.trim().toLowerCase();
    if (term && term !== lastTracked.current) {
      lastTracked.current = term;
      analytics.search(query.trim(), "global");
    }
  }, []);

  // Live search-as-you-type (debounced) so results appear without pressing Enter.
  useEffect(() => {
    const t = setTimeout(() => {
      if (q.trim()) {
        setParams({ q }, { replace: true });
        doSearch(q);
      } else {
        setResults(null);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [q, doSearch, setParams]);

  const submit = (e) => { e.preventDefault(); setParams({ q }); doSearch(q); };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="search-page">
      <div>
        <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold">Unified</div>
        <h1 className="font-heading text-3xl font-bold text-[#111827] mt-1">Search</h1>
      </div>

      <form onSubmit={submit} className="relative max-w-xl">
        <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search protocols, drugs, guides..." className="pl-9 h-11 bg-white" data-testid="search-input" autoFocus />
      </form>

      {!results && (
        <div className="text-sm text-slate-500" data-testid="search-hint">
          {q.trim() ? "Searching…" : "Start typing to search across protocols, drugs, calculators, guides and ICD-10 codes — results appear as you type."}
        </div>
      )}

      {results && (
        <div className="space-y-8">
          {sectionDefs.map((s) => {
            const list = results[s.key] || [];
            return (
              <div key={s.key}>
                <h3 className="font-heading font-semibold text-[#111827] mb-2">
                  {s.title} <span className="text-slate-400 text-sm font-normal">({list.length})</span>
                </h3>
                {list.length === 0 ? (
                  <div className="text-sm text-slate-500">No matches.</div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-3">
                    {list.map((item) => {
                      const id = item[s.idField];
                      const href = s.listOnly ? s.routePrefix : `${s.routePrefix}/${id}`;
                      return (
                        <Link key={id} to={href} data-testid={`search-${s.key}-${id}`}>
                          <Card className="border-slate-200 hover:border-[#0B5FFF]/40">
                            <CardContent className="p-4">
                              <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px] mb-1 capitalize">
                                {(item[s.metaField] || s.title).toString().replace(/_/g, " ")}
                              </Badge>
                              <div className="font-medium text-[#111827]">{item[s.titleField]}</div>
                              {item.definition && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{item.definition}</p>}
                              {item.indication && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{item.indication}</p>}
                            </CardContent>
                          </Card>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
